"use client";

import { useId, type ReactNode } from "react";
import { useFormattedNumberInput } from "@marketing-sites/hooks/use-formatted-number-input";
import { cn } from "@marketing-sites/utils/cn";
import { DrlpInfoModal } from "../../drlp/info-modal";

const INPUT_CLASS =
  "text-button-lg border-current/20 bg-[var(--drlp-contrast-surface,var(--color-white))] focus:border-current/50 w-full rounded-[2px] border px-3 py-4 font-sans outline-none";

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  prefix?: string;
  suffix?: string;
}

function NumberField({
  label,
  value,
  onChange,
  prefix,
  suffix,
}: NumberFieldProps) {
  const id = useId();
  const { displayValue, handleChange } = useFormattedNumberInput(value);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-body">
        {label}
      </label>
      <div className="relative">
        {prefix && (
          <span
            className="text-button-lg text-current/60 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          >
            {prefix}
          </span>
        )}
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={displayValue}
          onChange={(e) => handleChange(e.target.value, onChange)}
          className={cn(INPUT_CLASS, prefix && "pl-7", suffix && "pr-12")}
        />
        {suffix && (
          <span
            className="text-button-lg text-current/60 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
            aria-hidden="true"
          >
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

interface ResolutionRateSliderProps {
  value: number;
  average: number;
  onChange: (value: number) => void;
  accentColor: string;
  info: ReactNode;
  openMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
}

const THUMB_CLASS =
  "[&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-none [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-off-black [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-none [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-off-black [&::-moz-range-track]:bg-transparent";

function ResolutionRateSlider({
  value,
  average,
  onChange,
  accentColor,
  info,
  openMetadata,
  closeMetadata,
}: ResolutionRateSliderProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label htmlFor={id} className="text-body">
            Resolution rate
          </label>
          <DrlpInfoModal
            title="Resolution rate"
            triggerAriaLabel="More information about resolution rate"
            openMetadata={openMetadata}
            closeMetadata={closeMetadata}
          >
            {info}
          </DrlpInfoModal>
        </div>
        <span className="text-button-lg tabular-nums" aria-hidden="true">
          {value}%
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={1}
        max={100}
        value={value}
        aria-valuetext={`${value} percent`}
        onChange={(e) => onChange(Number(e.target.value) || 1)}
        className={cn(
          "h-[3px] w-full cursor-pointer appearance-none rounded-none focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current",
          THUMB_CLASS,
        )}
        style={{
          background: `linear-gradient(to right, ${accentColor} ${value}%, color-mix(in oklab, currentColor 15%, transparent) ${value}%)`,
        }}
      />
      <div
        className="text-label-lg text-current/50 relative flex justify-between"
        aria-hidden="true"
      >
        <span>1%</span>
        <span
          className="absolute top-0 -translate-x-1/2 whitespace-nowrap before:absolute before:bottom-full before:left-1/2 before:mb-1 before:h-3 before:border-l before:border-dashed before:border-current"
          style={{ left: `${average}%` }}
        >
          Avg.
        </span>
        <span>100%</span>
      </div>
    </div>
  );
}

export interface RoiCalculatorInputsProps {
  conversationsPerMonth: number;
  supportAgents: number;
  annualCostPerAgent: number;
  resolutionRate: number;
  averageResolutionRate: number;
  onConversationsPerMonthChange: (value: number) => void;
  onSupportAgentsChange: (value: number) => void;
  onAnnualCostPerAgentChange: (value: number) => void;
  onResolutionRateChange: (value: number) => void;
  accentColor: string;
  openMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
  className?: string;
}

export function RoiCalculatorInputs({
  conversationsPerMonth,
  supportAgents,
  annualCostPerAgent,
  resolutionRate,
  averageResolutionRate,
  onConversationsPerMonthChange,
  onSupportAgentsChange,
  onAnnualCostPerAgentChange,
  onResolutionRateChange,
  accentColor,
  openMetadata,
  closeMetadata,
  className,
}: RoiCalculatorInputsProps) {
  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <NumberField
        label="Conversations per month"
        value={conversationsPerMonth}
        onChange={onConversationsPerMonthChange}
        suffix="/mo"
      />
      <NumberField
        label="Customer support agents"
        value={supportAgents}
        onChange={onSupportAgentsChange}
      />
      <NumberField
        label="Annual cost per agent"
        value={annualCostPerAgent}
        onChange={onAnnualCostPerAgentChange}
        prefix="$"
        suffix="/yr"
      />
      <ResolutionRateSlider
        value={resolutionRate}
        average={averageResolutionRate}
        onChange={onResolutionRateChange}
        accentColor={accentColor}
        openMetadata={openMetadata}
        closeMetadata={closeMetadata}
        info={
          <p className="text-body max-w-[440px]">
            The share of conversations Fin resolves without a human. Most
            customers see about a {averageResolutionRate}% resolution rate with
            Fin.
          </p>
        }
      />
    </div>
  );
}
